import { Reveal } from "./Reveal";
import { motion } from "framer-motion";
import { Repeat, FlaskConical, Layers, Truck } from "lucide-react";
import { useLang } from "../i18n";

const FACILITY = "/about-facility.webp";

const ICONS = [Repeat, FlaskConical, Layers, Truck];

export const WhyClara = () => {
  const { t } = useLang();
  return (
  <section id="why" data-testid="why-section" className="bg-clara-cream py-24 lg:py-36">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <Reveal>
            <p className="eyebrow text-clara-terracotta mb-6">{t.why.eyebrow}</p>
            <h2 className="font-display font-light text-5xl lg:text-7xl leading-[0.95] tracking-tighter text-clara-espresso">
              {t.why.headingA}<span className="italic text-clara-terracotta">{t.why.headingEm}</span>{t.why.headingB}
            </h2>
            <p className="mt-8 text-clara-muted leading-relaxed max-w-md">
              {t.why.intro}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <div className="border-t border-clara-espresso/15">
            {t.why.chapters.map((c, i) => {
              const Icon = ICONS[i];
              return (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group grid grid-cols-[auto_1fr] gap-6 py-8 border-b border-clara-espresso/15" data-testid={`why-chapter-${i}`}>
                  <span className="font-data text-sm text-clara-terracotta pt-2">0{i + 1}</span>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className="w-5 h-5 text-clara-espresso" strokeWidth={1.5} />
                      <h3 className="font-display text-2xl lg:text-3xl text-clara-espresso">{c.title}</h3>
                    </div>
                    <p className="text-clara-muted leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

export const About = ({ h1 = false }) => {
  const { t } = useLang();
  const H = h1 ? "h1" : "h2";
  return (
  <section id="about" data-testid="about-section" className="bg-clara-cream py-24 lg:py-36">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="relative overflow-hidden">
          <div className="overflow-hidden">
            <motion.img
              initial={{ scale: 1.2 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              src={FACILITY}
              alt="Clara production facility"
              loading="lazy"
              decoding="async"
              className="w-full h-[520px] object-cover"
            />
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="eyebrow text-clara-terracotta mb-6">{t.about.eyebrow}</p>
            <H className="font-display font-light text-4xl lg:text-6xl leading-[0.98] tracking-tighter text-clara-espresso mb-8">
              {t.about.heading}
            </H>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-clara-muted leading-relaxed mb-5">
              {t.about.p1}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="border-l-2 border-clara-terracotta pl-6 py-1 mb-8">
              <p className="text-clara-espresso leading-relaxed">
                {t.about.p2}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="grid grid-cols-3 gap-6">
              {[["9", t.about.stats[0]], ["5–11", t.about.stats[1]], ["99.5%", t.about.stats[2]]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-4xl lg:text-5xl text-clara-terracotta">{n}</div>
                  <div className="font-data text-[10px] tracking-[0.15em] uppercase text-clara-muted mt-1">{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
  );
};
